/**
 * BarLevelPrimitive — a per-bar level drawn ACROSS ITS OWN BAR.
 *
 * Why not a LineSeries with `lineType: WithSteps`? lightweight-charts draws a
 * step from point i's x-CENTRE to point i+1's x-centre and only then jumps. So
 * over any candle the LEFT half shows the previous bar's level and only the right
 * half shows its own — half a bar out of register. For a rule whose whole claim
 * is "this bar breached THIS bar's level", reading the wick against the line then
 * compares the bar with a level that belonged to its neighbour. (Measured on the
 * CLN_Break chart, 2026-09-24; the library source, lightweight-charts 5.0.9
 * `walkLine`, draws `lineTo(x[i], y[i-1])` then `lineTo(x[i], y[i])`.)
 *
 * Here each level is a horizontal segment spanning [x - barSpacing/2,
 * x + barSpacing/2] of the bar it governs, and consecutive bars are joined by a
 * vertical riser on their shared boundary. The series this is attached to keeps
 * its axis label and autoscale; its own line is hidden (`lineVisible: false`).
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface BarLevelPoint {
  time: number;
  value: number;
  /** Per-bar colour (overrides `opts.color`) — e.g. a band coloured by its own
   *  direction. The riser into a bar takes that bar's colour. */
  color?: string;
  /** Start a new segment here: no riser from the previous point. Lets one
   *  primitive carry many trades whose lines share a reversal bar. */
  newSeg?: boolean;
  /** Text drawn at this bar's left end, in its colour — above the line, or
   *  below it with `labelBelow` (so two lines that start on the same bar can put
   *  their labels on opposite sides instead of on top of each other). */
  label?: string;
  labelBelow?: boolean;
}

export interface BarLevelOptions {
  color: string;
  width?: number;
  /** lightweight-charts LineStyle: 0 solid, 1 dotted, 2 dashed, 3 large dashed, 4 sparse dotted. */
  style?: number;
  /** Plate behind label text (the chart background, translucent), so a label
   *  over candles stays readable. None when omitted. */
  labelBg?: string;
}

function dash(style: number, w: number): number[] {
  switch (style) {
    case 1: return [w, w];
    case 2: return [2 * w, 2 * w];
    case 3: return [6 * w, 6 * w];
    case 4: return [w, 4 * w];
    default: return [];
  }
}

class BarLevelRenderer implements IPrimitivePaneRenderer {
  constructor(private readonly p: BarLevelPrimitive) {}

  draw(target: CanvasRenderingTarget2D): void {
    const { chart, series, points, opts } = this.p;
    if (!chart || !series || points.length === 0) return;
    const ts = chart.timeScale();
    let spacing = 6;
    try { spacing = Number((ts.options() as any).barSpacing) || 6; } catch { /* default */ }
    const half = spacing / 2;

    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      const w = Math.max(1, Math.round((opts.width || 2) * Math.min(hpr, vpr)));
      ctx.lineWidth = w;
      ctx.strokeStyle = opts.color;
      ctx.setLineDash(dash(opts.style || 0, w));
      ctx.lineCap = 'butt';
      // Consecutive same-colour bars share one path; a colour change strokes
      // what is open and starts again, riser included in the NEW colour.
      let pathColor: string | null = null;
      const flush = (): void => { if (pathColor != null) ctx.stroke(); pathColor = null; };
      const labels: Array<{ x: number; y: number; text: string; color: string; below: boolean }> = [];
      let prev: { xr: number; y: number } | null = null;
      for (const pt of points) {
        if (pt.newSeg) prev = null;
        const xc = ts.timeToCoordinate(pt.time as Time);
        const yc = series.priceToCoordinate(pt.value);
        if (xc == null || yc == null) { prev = null; continue; }
        const xl = (Number(xc) - half) * hpr;
        const xr = (Number(xc) + half) * hpr;
        if (xr < 0 || xl > bitmapSize.width) { prev = null; continue; }
        const y = Math.round(Number(yc) * vpr) + (w % 2 ? 0.5 : 0);
        const color = pt.color || opts.color;
        if (color !== pathColor) {
          flush();
          ctx.strokeStyle = color;
          ctx.beginPath();
          pathColor = color;
        }
        // Riser on the shared boundary, only when this bar directly follows the
        // previous one (a gap in the points is a gap in the line).
        if (prev && Math.abs(prev.xr - xl) < 1.5 * hpr) {
          ctx.moveTo(prev.xr, prev.y);
          ctx.lineTo(xl, y);
        } else {
          ctx.moveTo(xl, y);
        }
        ctx.lineTo(xr, y);
        prev = { xr, y };
        if (pt.label) labels.push({ x: Math.max(xl, 2 * hpr), y, text: pt.label, color, below: !!pt.labelBelow });
      }
      flush();
      if (labels.length) {
        ctx.setLineDash([]);
        ctx.font = Math.round(10 * vpr) + 'px sans-serif';
        ctx.textAlign = 'left';
        const fh = Math.round(10 * vpr);
        const pad = Math.round(2 * hpr);
        for (const l of labels) {
          ctx.textBaseline = l.below ? 'top' : 'bottom';
          const ty = l.below ? l.y + 3 * vpr : l.y - 3 * vpr;
          if (opts.labelBg) {
            const tw = ctx.measureText(l.text).width;
            ctx.fillStyle = opts.labelBg;
            ctx.fillRect(l.x - pad, l.below ? ty - vpr : ty - fh - vpr, tw + 2 * pad, fh + 2 * vpr);
          }
          ctx.fillStyle = l.color;
          ctx.fillText(l.text, l.x, ty);
        }
      }
      ctx.restore();
    });
  }
}

class BarLevelPaneView implements IPrimitivePaneView {
  constructor(private readonly p: BarLevelPrimitive) {}
  zOrder() { return 'normal' as const; }
  renderer(): IPrimitivePaneRenderer | null { return new BarLevelRenderer(this.p); }
}

export class BarLevelPrimitive implements ISeriesPrimitive<Time> {
  points: BarLevelPoint[];
  opts: BarLevelOptions;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _views: BarLevelPaneView[];

  constructor(points: BarLevelPoint[], opts: BarLevelOptions) {
    this.points = points;
    this.opts = opts;
    this._views = [new BarLevelPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void { this.chart = null; this.series = null; }
  updateAllViews(): void { /* the renderer reads this.points directly */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._views; }
}
